import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Copy,
    WhatsappLogo,
    Code,
    ClipboardText,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { Spinner, Button } from '@/components/ui';
import { QRCode } from '@/components/features/QRCode/QRCode';
import { useForm } from '@/api';
import { buildFormUrl, ROUTES } from '@/constants';
import { buildPrefillUrl } from '@/api/useFormShare';

import styles from './FormShare.module.css';

export function FormShare() {
    const { userSlug, formSlug } = useParams<{ userSlug: string; formSlug: string }>();
    const navigate = useNavigate();

    const { data: form, isLoading, error } = useForm(formSlug || '');

    const [prefills, setPrefills] = useState<Record<string, string>>({});
    const [showEmbed, setShowEmbed] = useState(false);

    useEffect(() => {
        if (form) {
            document.title = `Compartir — ${form.title} — Ratio`;
        }
    }, [form]);

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (error || !form) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Formulario no encontrado</h2>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    const formBaseUrl = `${window.location.origin}${buildFormUrl(userSlug || form.author.slug, form.slug)}`;
    const prefillUrl = buildPrefillUrl(formBaseUrl, prefills);
    const embedCode = `<iframe src="${formBaseUrl}" width="100%" height="600" frameborder="0"></iframe>`;

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(formBaseUrl);
            toast.success('Enlace copiado');
        } catch {
            toast.error('No se pudo copiar el enlace');
        }
    };

    const handleCopyPrefill = async () => {
        try {
            await navigator.clipboard.writeText(prefillUrl);
            toast.success('Enlace copiado');
        } catch {
            toast.error('No se pudo copiar el enlace');
        }
    };

    const handleCopyEmbed = async () => {
        try {
            await navigator.clipboard.writeText(embedCode);
            toast.success('Enlace copiado');
        } catch {
            toast.error('No se pudo copiar el codigo');
        }
    };

    const handleWhatsApp = () => {
        window.open(
            `https://wa.me/?text=${encodeURIComponent(`Responde mi formulario "${form.title}": ${formBaseUrl}`)}`,
            '_blank'
        );
    };

    // Get all fields across sections for prefill builder
    const allFields = form.sections?.length > 0
        ? form.sections.flatMap((s) => s.fields)
        : form.fields;

    return (
        <div className={styles.page}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Volver">
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.content}>
                <div className={styles.header}>
                    <ClipboardText size={32} weight="duotone" className={styles.headerIcon} />
                    <div>
                        <h1 className={styles.title}>Compartir formulario</h1>
                        <p className={styles.subtitle}>{form.title}</p>
                    </div>
                </div>

                {/* URL row */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Enlace directo</h2>
                    <div className={styles.urlRow}>
                        <div className={styles.urlBox}>
                            <span className={styles.urlText}>{formBaseUrl}</span>
                        </div>
                        <button type="button" className={styles.copyBtn} onClick={handleCopyLink}>
                            <Copy size={16} weight="bold" />
                            Copiar
                        </button>
                    </div>

                    <div className={styles.shareActions}>
                        <button type="button" className={styles.whatsappBtn} onClick={handleWhatsApp}>
                            <WhatsappLogo size={18} weight="fill" />
                            Compartir por WhatsApp
                        </button>
                        <button
                            type="button"
                            className={styles.viewBtn}
                            onClick={() => navigate(buildFormUrl(userSlug || form.author.slug, form.slug))}
                        >
                            Ver formulario
                        </button>
                    </div>
                </div>

                {/* QR Code */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Codigo QR</h2>
                    <QRCode value={formBaseUrl} size={200} />
                    <p className={styles.qrHint}>Escanea el codigo para abrir el formulario</p>
                </div>

                {/* Pre-filled link builder */}
                {allFields.length > 0 && (
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>Constructor de enlace prellenado</h2>
                        <p className={styles.sectionDesc}>
                            Completa los valores para generar un enlace que rellena respuestas automaticamente.
                        </p>
                        <div className={styles.prefillFields}>
                            {allFields.slice(0, 10).map((field) => (
                                <div key={field.id} className={styles.prefillRow}>
                                    <label className={styles.prefillLabel}>{field.label}</label>
                                    <input
                                        className={styles.prefillInput}
                                        value={prefills[String(field.id)] ?? ''}
                                        onChange={(e) =>
                                            setPrefills((prev) => ({
                                                ...prev,
                                                [String(field.id)]: e.target.value,
                                            }))
                                        }
                                        placeholder="Valor prellenado (opcional)"
                                    />
                                </div>
                            ))}
                        </div>

                        {Object.values(prefills).some((v) => v) && (
                            <div className={styles.prefillResult}>
                                <div className={styles.urlBox}>
                                    <span className={styles.urlText}>{prefillUrl}</span>
                                </div>
                                <button type="button" className={styles.copyBtn} onClick={handleCopyPrefill}>
                                    <Copy size={16} weight="bold" />
                                    Copiar enlace
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Embed code */}
                <div className={styles.section}>
                    <button
                        type="button"
                        className={styles.embedToggle}
                        onClick={() => setShowEmbed((v) => !v)}
                    >
                        <Code size={16} />
                        {showEmbed ? 'Ocultar codigo de insercion' : 'Mostrar codigo de insercion'}
                    </button>
                    {showEmbed && (
                        <div className={styles.embedArea}>
                            <pre className={styles.embedCode}>{embedCode}</pre>
                            <button type="button" className={styles.copyBtn} onClick={handleCopyEmbed}>
                                <Copy size={16} weight="bold" />
                                Copiar codigo
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
